import { FormSectionHandler } from '../entity.handler';
import { MapSetResponse, MapsetUpdateRequest, MapSetLayer } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, NumberField, ComboField, MemoField, EMPTY_GUID } from '../utils';
import { ICONS } from '../icons';
import * as uuid from 'uuid';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { cloneDeep } from 'lodash';

type LayerType = 'layer' | 'overlay';

interface EditorMapSetLayer {
    name: string;
    projection: string;
    minZoom: number;
    maxZoom: number;
    serverType: 'google' | 'wms' | 'osm' | 'wmts' | 'dds' | 'tms' | 'mbs';
    order: number;
    imageType: 'png' | 'jpg' | 'jpeg' | 'gif' | 'mvt';
    layerName: string;
    servers: Array<string>;
    overlays: ListFieldValue<EditorMapSetLayer>[];
    attribution?: string;
}

export interface EditorValues {
    name?: string;
    state?: 'active' | 'deleted';
    layers?: ListFieldValue<EditorMapSetLayer>[];
    overlays?: ListFieldValue<EditorMapSetLayer>[];
}

export class MapSetDetailsSectionHandler implements FormSectionHandler<MapSetResponse, EditorValues> {

    type = 'form';
    icon = ICONS.mapset;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean,
        private handler: any // type is MapSetHandler, but importing it here creates circular dependency
    ) {
    }

    private properties = ['name', 'state', 'layers', 'overlays'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.MAPSET.DETAILS.${key}`, values);
    }

    applyUpdate(entity: MapSetResponse, payload: MapsetUpdateRequest): Promise<MapSetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateMapSet(entity.id, payload));
    }

    mapEntityToEditor(entity: MapSetResponse): Promise<EditorValues> {
        const result: EditorValues = {
            name: entity.name,
            state: entity.state,
            layers: this.stateItemsToListItems(entity.layers),
            overlays: this.stateItemsToListItems(entity.overlays),
        };
        return Promise.resolve(EntityUtils.mergeFields(this.properties, result, {}));
    }

    mapEditorToEntity(entity: MapsetUpdateRequest, values: EditorValues): Promise<MapsetUpdateRequest> {
        const result = EntityUtils.mergeFields<MapsetUpdateRequest>(this.properties, {
            ...values,
            layers: values.layers && this.listItemsToStateItems(values.layers),
            overlays: values.overlays && this.listItemsToStateItems(values.overlays),
        }, entity);
        return Promise.resolve(result);
    }

    getFields(entity: MapSetResponse): Promise<FormBuilderField[]> {
        const getText = (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', ');

        return Promise.resolve([
            TextField('name', this.translate('FIELDS.NAME'), true, null, null),
            {
                id: 'layers', title: this.translate('FIELDS.LAYERS'), type: 'list', required: false, getText,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: async (_action, _field, values: EditorValues) => this.addOrEditMainLayer('layer', values, {} as MapSetLayer, EMPTY_GUID) },
                    { id: 'edit', click: async (_action, _field, mapset: EditorValues, layer: ListFieldValue<EditorMapSetLayer>) => this.addOrEditMainLayer('layer', mapset, layer.data, layer.id) },
                ],
            },
            {
                id: 'overlays', title: this.translate('FIELDS.OVERLAYS'), type: 'list', required: false, getText,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: async (_action, _field, values: EditorValues) => this.addOrEditMainLayer('overlay', values, {} as MapSetLayer, EMPTY_GUID) },
                    { id: 'edit', click: async (_action, _field, mapset: EditorValues, layer: ListFieldValue<EditorMapSetLayer>) => this.addOrEditMainLayer('overlay', mapset, layer.data, layer.id) },
                ],
            },
        ]).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

    async showLayerModal(type: LayerType, values: EditorMapSetLayer, entity: EditorValues, parentId: string): Promise<ListFieldValue<EditorMapSetLayer>> {
        const addOrEditChildLayer = async (parentLayer: EditorMapSetLayer, layer: ListFieldValue<EditorMapSetLayer>) => {
            const updatedLayer = await this.showLayerModal('overlay', layer.data || {} as EditorMapSetLayer, entity, null);

            // if we have a layer we should add/update it to our parent layer's overlays
            if (updatedLayer) {
                parentLayer.overlays = parentLayer.overlays || [];
                const overlayIndex = this.getLayerIndex(parentLayer.overlays, layer.id);
                parentLayer.overlays[overlayIndex] = updatedLayer;
            }

            // open the edit modal of our parent layer with the updated layer
            this.addOrEditMainLayer(type, entity, parentLayer, parentId);
        };

        const result = await this.admin.showFormModal<EditorMapSetLayer>({
            groups: [{
                name: this.translate(`MODAL.TITLE.${type.toUpperCase()}`),
                description: this.translate(`MODAL.DESC.${type.toUpperCase()}`),
                fields: [
                    TextField('name', this.translate('MODAL.FIELDS.NAME'), true, null, null),
                    TextField('attribution', this.translate('MODAL.FIELDS.ATTRIBUTION'), false, null, null),
                    NumberField('order', this.translate('MODAL.FIELDS.ORDER'), true, 0, null, ''),
                    NumberField('minZoom', this.translate('MODAL.FIELDS.MIN_ZOOM'), true, 0, null, ''),
                    NumberField('maxZoom', this.translate('MODAL.FIELDS.MAX_ZOOM'), true, 0, null, ''),
                    ComboField('projection', this.translate('MODAL.FIELDS.PROJECTION'), true, [
                        { key: 'EPSG:900913', value: 'Mercator' },
                        { key: 'EPSG:4326', value: 'WGS84' },
                    ]),
                    ComboField('imageType', this.translate('MODAL.FIELDS.IMAGE_TYPE'), true, [
                        { key: 'png', value: 'PNG' },
                        { key: 'jpg', value: 'JPG' },
                        { key: 'jpeg', value: 'JPEG' },
                        { key: 'gif', value: 'GIF' },
                        { key: 'mvt', value: 'MVT' },
                    ]),
                    TextField('layerName', this.translate('MODAL.FIELDS.LAYER_NAME'), true, null, null),
                    ComboField('serverType', this.translate('MODAL.FIELDS.SERVER_TYPE'), true, [
                        { key: 'google', value: 'Google' },
                        { key: 'wms', value: 'WMS' },
                        { key: 'osm', value: 'OSM' },
                        { key: 'wmts', value: 'WMTS' },
                        { key: 'dds', value: 'DDS' },
                        { key: 'tms', value: 'TMS' },
                        { key: 'mbs', value: 'MapBox Style' },
                    ]),
                    MemoField('servers', this.translate('MODAL.FIELDS.SERVERS'), true, null, null),
                    {
                        id: 'overlays', title: this.translate('FIELDS.OVERLAYS'), type: 'list', required: false, visible: !!parentId,
                        actions: [
                            { name: this.admin.i18n.instant('SHARED.ADD'), click: async (_action, _field, parentLayer: EditorMapSetLayer) => addOrEditChildLayer(parentLayer, {} as MapSetLayer) },
                            { id: 'edit', click: async (_action, _field, parentLayer: EditorMapSetLayer, layer: ListFieldValue<EditorMapSetLayer>) => addOrEditChildLayer(parentLayer, layer)},
                        ],
                    },
                ],
            }],
        }, values);

        if (result) {
            return {
                id: uuid.v4(),
                name: result.name,
                icon: ICONS.mapset,
                data: result,
            };
        }

    }

    stateItemToListItem(layer: MapSetLayer): ListFieldValue<EditorMapSetLayer> {
        return {
            id: uuid.v4(),
            name: layer.name,
            icon: ICONS.mapset,
            data: layer,
        };
    }

    stateItemsToListItems(layers: MapSetLayer[] = []): ListFieldValue<EditorMapSetLayer>[] {
        const result = cloneDeep(layers);
        // layers can have overlays inside so we need to check for if it is present and then convert those too
        const recurseStateItemToListItem = (items = []) => {
            items.forEach((x, i) => {
                items[i] = this.stateItemToListItem(x);

                if (items[i].data.overlays && !!items[i].data.overlays.length) {
                    recurseStateItemToListItem(items[i].data.overlays);
                }
            });
        };
        recurseStateItemToListItem(result);
        return result;
    }

    listItemsToStateItems(list: ListFieldValue<EditorMapSetLayer>[] = []): MapSetLayer[] {
        const result = cloneDeep(list);
        // layers can have overlays inside so we need to check for if it is present and then convert those too
        const recurseListItemToStateItem = (items = []) => {
            items.forEach((x, i) => {
                items[i] = x.data;

                // avoid length validation errors from server by just deleting empty string attributions
                if (items[i].attribution === '') {
                    delete items[i].attribution;
                }
                if (items[i].overlays && !!items[i].overlays.length) {
                    recurseListItemToStateItem(items[i].overlays);
                }
            });
        };
        recurseListItemToStateItem(result);
        return result as MapSetLayer[];
    }

    private async addOrEditMainLayer(type: LayerType, values: EditorValues, layer: EditorMapSetLayer, id: string) {
        const key = type + 's';
        const layerIndex = this.getLayerIndex(values[key], id);

        // send EMPTY_GUID for a new layer so showLayerModal can know that this is a child layer form
        const updatedLayer = await this.showLayerModal(type, layer || {} as EditorMapSetLayer, values, id || EMPTY_GUID);
        if (updatedLayer) {
            values[key][layerIndex] = updatedLayer;
        }

        // on creation we have to reopen the create wizard with the updated entity
        if (this.creating) {
            this.handler.entityDefaults = await this.mapEditorToEntity({}, values);
            await this.admin.showCreateWizardModal(this.ownerId, this.handler);
            // clear defaults after wizard closed
            this.handler.entityDefaults = null;
        }
    }

    private getLayerIndex(layers: { id?: string }[], id: string): number {
        const index = (layers || []).findIndex(x => x.id === id);
        // EMPTY_GUID is a placeholder for a new main item before it is created
        return (index > -1 && id !== EMPTY_GUID) ? index : (layers || []).length;
    }
}
